import React from "react";
import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import Navigation from "./Navigation";
import Budget from "./Budget";
import Legal from "./Legal";
import axios from "axios";
import { useRollbar } from "@rollbar/react";
import styles from "./Settings.module.scss";
import { useTrackPageVisit } from "../hooks/useTrackPageVisit";
import { config } from "../config";
import { dollars, wholeNumber } from "../services/Filters";

interface SettingsProps {
  onAlert: (message: string, type: string) => void;
}

interface SettingsState {
  subscribed: null | boolean;
  emailFrequency: string;
  subscriptionType: string;
  nextMailingDate: null;
}

interface BudgetState {
  budget: number | null;
  previousBudget: number | null;
  currentWeekAverage: number | null;
}

const serverUrl = config.apiURL;

const SETTING_HEADER_IMAGE =
  "https://billcap-cdn.s3.ap-southeast-2.amazonaws.com/Engie/tracker/setting_header.png";
const SETTING_HEADER_BACKGROUND =
  "https://billcap-cdn.s3.ap-southeast-2.amazonaws.com/Engie/tracker/setting_header_background.png";

function getMonday(date: Date) {
  const day = date.getDay() || 7;
  if (day !== 1) {
    date.setHours(-24 * (day - 1) + date.getHours());
  }
  return date;
}

function formatDate(dateString: string | number | Date) {
  const date = new Date(dateString);
  const year = date.getFullYear();
  let month = String(date.getMonth() + 1);
  if (month.length === 1) month = "0" + month;
  let dayOfMonth = String(date.getDate());
  if (dayOfMonth.length === 1) dayOfMonth = "0" + dayOfMonth;
  return `${year}-${month}-${dayOfMonth}`;
}

const Settings: React.FC<SettingsProps> = (props) => {
  const rollbar = useRollbar();
  const { accountId } = useParams();
  const { onAlert } = props;
  useTrackPageVisit("settings", accountId as string);

  const [states, setStates] = useState<SettingsState>({
    subscribed: null,
    emailFrequency: "weekly",
    subscriptionType: "Subscriptions::WeeklyEmail",
    nextMailingDate: null,
  });

  const [budgetState, setBudgetState] = useState<BudgetState>({
    budget: null,
    previousBudget: null,
    currentWeekAverage: null,
  });

  const fetchBudgetData = useCallback(
    function fetchBudgetData() {
      const from = formatDate(getMonday(new Date()));
      axios({
        method: "get",
        baseURL: serverUrl,
        url: "/api/tracker/usage",
        params: { account_id: accountId, from },
      })
        .then((response) => {
          const customer = response.data?.customer;
          const currentWeek = response.data?.current_week;
          const budget = customer?.budget ?? null;
          const currentWeekAverage =
            currentWeek?.insights?.weekly_average_spend ?? null;
          setBudgetState({
            budget,
            previousBudget: budget,
            currentWeekAverage,
          });
        })
        .catch(() => {
          setBudgetState((prev) => ({ ...prev }));
        });
    },
    [accountId]
  );

  useEffect(() => {
    if (accountId) fetchBudgetData();
  }, [accountId, fetchBudgetData]);

  function updateBudget(value: number) {
    setBudgetState((prev) => ({ ...prev, budget: value }));
  }

  function saveBudget() {
    axios({
      method: "post",
      baseURL: serverUrl,
      url: "/api/settings/spend_target",
      params: { account_id: accountId },
      data: { spend_target: budgetState.budget },
    })
      .then(() => {
        window.localStorage.clear();
        if ((budgetState.budget as number) > 0) {
          onAlert(
            `Budget updated to $${budgetState.budget} per week.`,
            "warning"
          );
        } else {
          onAlert("Budget setting disabled.", "error");
        }
        setBudgetState((prev) => ({
          ...prev,
          previousBudget: prev.budget,
        }));
      })
      .catch((e) => {
        onAlert("Error saving budget data. Please try again.", "warning");
        rollbar.error("Ajax request error (set budget data)", e);
      });
  }

  const fetchSubscriptionDataError = useCallback(
    function fetchSubscriptionDataError(e: Error) {
      if (/403/.test(e.message)) {
        onAlert("This account does not belong to this provider", "warning");
      } else {
        onAlert(
          "Error fetching subscription data. Please try again.",
          "warning"
        );
      }
      rollbar.error("Ajax request error (fetch subscription data)", e);
    },
    [onAlert, rollbar]
  );

  const fetchSubscriptionData = useCallback(
    function fetchSubscriptionData() {
      axios({
        method: "get",
        baseURL: serverUrl,
        url: "/api/tracker/subscription",
        params: { account_id: accountId },
      })
        .then((response) => {
          let subscriptionData = response.data;
          let subscribed = false;
          if (subscriptionData && subscriptionData.subscribed) {
            subscribed = subscriptionData.subscribed;
          }
          let subscriptionType = "Subscriptions::WeeklyEmail";
          if (subscriptionData && subscriptionData.type) {
            subscriptionType = subscriptionData.type;
          }
          let emailFrequency: string;
          if (subscriptionType === "Subscriptions::WeeklyEmail") {
            emailFrequency = "weekly";
          } else {
            emailFrequency = "monthly";
          }
          setStates((prevStates) => ({
            ...prevStates,
            subscribed: subscribed,
            subscriptionType: subscriptionType,
            emailFrequency: emailFrequency,
            nextMailingDate: subscriptionData.next_mailing_date,
          }));
        })
        .catch((e) => fetchSubscriptionDataError(e));
    },
    [accountId, fetchSubscriptionDataError]
  );

  function subscribe() {
    updateSubscription(true);
  }
  function unsubscribe() {
    updateSubscription(false);
  }

  function updateSubscription(subscribed: boolean) {
    axios({
      method: "post",
      baseURL: serverUrl,
      url: "/api/tracker/subscription",
      data: {
        account_id: accountId,
        data: {
          type: states.subscriptionType,
          subscribed: subscribed,
        },
      },
    })
      .then((response) => {
        setStates({ ...states, subscribed: response.data.subscribed });
        onAlert("Weekly email subscription updated.", "warning");
      })
      .catch((e) => updateSubscriptionError(e));
  }

  function updateSubscriptionError(e: Error) {
    onAlert("Error saving subscription data. Please try again.", "warning");
    rollbar.error("Ajax request error (set subscription data)", e);
  }

  function switchFrequency() {
    axios({
      method: "put",
      baseURL: serverUrl,
      url: "/api/tracker/subscription/change_frequency",
      data: {
        account_id: accountId,
      },
    })
      .then((response) => switchFrequencyResponse(response))
      .catch((e) => switchFrequencyError(e));
  }
  function switchFrequencyResponse(response: {
    data: {
      subscribed: boolean;
      type: string;
    };
  }) {
    try {
      let subscribed = response.data.subscribed;
      let subscriptionType = response.data.type;
      let emailFrequency: string;
      if (subscriptionType === "Subscriptions::WeeklyEmail") {
        emailFrequency = "weekly";
      } else {
        emailFrequency = "monthly";
      }
      setStates({
        ...states,
        subscribed: subscribed,
        subscriptionType: subscriptionType,
        emailFrequency: emailFrequency,
      });
      onAlert(
        `Email subscription frequency updated to ${emailFrequency}.`,
        "warning"
      );
      fetchSubscriptionData();
    } catch (e) {
      rollbar.error("Error switching tracker frequency", e as Error);
    }
  }

  function switchFrequencyError(e: Error) {
    onAlert("Error saving subscription data. Please try again.", "warning");
    rollbar.error("Ajax request error (set subscription data)", e);
  }

  const averageWeeklyDisplay =
    budgetState.currentWeekAverage != null
      ? dollars(wholeNumber(budgetState.currentWeekAverage), 0)
      : "$-";

  useEffect(() => {
    fetchSubscriptionData();
  }, [fetchSubscriptionData]);

  return (
    <div className={styles.settings}>
      <Navigation />
      <div className={styles.main}>
        <div className={styles.mainInner}>
          {accountId && (
            <section className={styles.hero} aria-labelledby="budget-heading">
              <div className={styles.heroText}>
                <h2 id="budget-heading" className={styles.heroTitle}>
                  Set your weekly
                  <br />
                  <span className={styles.heroTitleAccent}>energy budget</span>
                </h2>
                <div className={styles.heroBody}>
                  <p>Want to keep track of your energy spend?</p>
                  <p>
                    Set a weekly budget and your Tracker emails will show how
                    you&apos;re going against it.
                  </p>
                  <p>
                    As a guide, your average weekly spend over the last{" "}
                    4 weeks has been{" "}
                    {averageWeeklyDisplay}.
                  </p>
                </div>
                <Budget
                  accountId={accountId}
                  currentWeekAverage={budgetState.currentWeekAverage}
                  budget={budgetState.budget}
                  previousBudget={budgetState.previousBudget}
                  updateBudget={updateBudget}
                  saveBudget={saveBudget}
                  variant="hero"
                />
              </div>
              <div className={styles.heroArt}>
                <div className={styles.heroArtFrame}>
                  <div
                    className={styles.heroArtBg}
                    style={{
                      backgroundImage: `url(${SETTING_HEADER_BACKGROUND})`,
                    }}
                    aria-hidden="true"
                  />
                  <img
                    className={styles.heroArtImg}
                    src={SETTING_HEADER_IMAGE}
                    alt=""
                    width={716}
                    height={618}
                    decoding="async"
                  />
                </div>
              </div>
            </section>
          )}

          {states.subscribed === null ? (
            <div className={styles.subscriptionError}>
              <h2>Failed to fetch subscription data. Please try again.</h2>
            </div>
          ) : states.subscribed ? (
            <section
              className={styles.emailSection}
              aria-label="Email preferences"
            >
              <div className={styles.emailRow}>
                <div className={styles.emailRowText}>
                  <h3 className={styles.emailRowTitle}>Want fewer emails?</h3>
                  {states.emailFrequency === "weekly" ? (
                    <p className={styles.emailRowBody}>
                      You can switch to{" "}
                      <strong>monthly Tracker emails</strong> instead.
                    </p>
                  ) : (
                    <p className={styles.emailRowBody}>
                      You can switch to{" "}
                      <strong>weekly Tracker emails</strong> instead.
                    </p>
                  )}
                </div>
                <div className={styles.emailRowActions}>
                  <button
                    type="button"
                    className={styles.primaryButton}
                    onClick={switchFrequency}
                  >
                    {states.emailFrequency === "weekly"
                      ? "SWITCH TO MONTHLY UPDATES"
                      : "SWITCH TO WEEKLY UPDATES"}
                  </button>
                </div>
              </div>
              <hr className={styles.emailDivider} />
              <div className={styles.emailRow}>
                <div className={styles.emailRowText}>
                  <h3 className={styles.emailRowTitle}>
                    Prefer not to receive Tracker emails anymore?
                  </h3>
                </div>
                <div className={styles.emailRowActions}>
                  <button
                    type="button"
                    className={styles.primaryButton}
                    onClick={unsubscribe}
                  >
                    Unsubscribe from Tracker emails
                  </button>
                </div>
              </div>
            </section>
          ) : (
            <section
              className={styles.emailSection}
              aria-label="Email preferences"
            >
              <div className={styles.emailRow}>
                <div className={styles.emailRowText}>
                  <h3 className={styles.emailRowTitle}>
                    Would you like to start receiving Tracker emails again?
                  </h3>
                </div>
                <div className={styles.emailRowActions}>
                  <button
                    type="button"
                    className={styles.primaryButton}
                    onClick={subscribe}
                  >
                    RESUBSCRIBE TO TRACKER EMAILS
                  </button>
                </div>
              </div>
            </section>
          )}
        </div>
      </div>

      {accountId && (
        <Legal accountId={accountId} hideSettings={true} />
      )}
    </div>
  );
};

export default Settings;
